import {useTheme} from '@primer/react-brand/esm'
import {useEffect, useEffectEvent, useRef, type SyntheticEvent} from 'react'
import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'

import type {HeroSequenceProps} from './types'

export function useHeroSequencePlayback({
  video,
  currentChapterIndex,
  onNextChapter,
  onReady,
  onPlaybackChange,
  videoRef,
  playbackStateRef,
  seekRequest,
}: Omit<HeroSequenceProps, 'chaptersAltDesc' | 'labelledBy'>) {
  const {colorMode} = useTheme()
  const prefersReducedMotion = usePrefersReducedMotion()
  const videoUrl = colorMode === 'dark' ? video?.url.dark || video?.url.light : video?.url.light
  const loadedSourceRef = useRef<string | null>(null)
  const reportedChapterRef = useRef(currentChapterIndex)
  const appliedSeekRequestRef = useRef(seekRequest)
  const isPlayingRef = useRef(false)

  const updatePlayback = (media: HTMLVideoElement, eventType: string) => {
    if (loadedSourceRef.current !== media.getAttribute('src')) return

    if (eventType === 'playing') isPlayingRef.current = true
    if (['pause', 'waiting', 'seeking', 'ended', 'error'].includes(eventType)) isPlayingRef.current = false

    const state = {
      currentTime: media.currentTime,
      isPaused: media.paused,
      isPlaying: isPlayingRef.current && !media.paused && !media.ended,
      isFinished: media.ended,
    }
    playbackStateRef.current = state
    onPlaybackChange(state)

    const chapterIndex = video?.chapterTimestamps.findLastIndex(timestamp => timestamp <= media.currentTime) ?? -1
    if (chapterIndex >= 0 && chapterIndex !== reportedChapterRef.current) {
      reportedChapterRef.current = chapterIndex
      onNextChapter(chapterIndex)
    }
  }

  const reportPlayback = (event: SyntheticEvent<HTMLVideoElement>) => updatePlayback(event.currentTarget, event.type)

  const initializeMedia = useEffectEvent(async (media: HTMLVideoElement) => {
    const source = media.getAttribute('src')
    if (media.readyState < media.HAVE_METADATA || source !== videoUrl || loadedSourceRef.current === source) {
      return
    }
    const timestamps = video?.chapterTimestamps
    if (!timestamps) return
    if (
      !Number.isFinite(media.duration) ||
      timestamps.some(
        (timestamp, index) =>
          !Number.isFinite(timestamp) ||
          timestamp < 0 ||
          timestamp >= media.duration ||
          timestamp <= (timestamps[index - 1] ?? -1),
      )
    ) {
      window.reportError(new Error('Hero video chapter timestamps must be increasing and within the video duration.'))
      return
    }

    const previousPosition = playbackStateRef.current
    const hasPendingSeek =
      appliedSeekRequestRef.current !== seekRequest || reportedChapterRef.current !== currentChapterIndex
    let currentTime = timestamps[currentChapterIndex] ?? 0
    if (!hasPendingSeek && previousPosition) {
      currentTime = previousPosition.isFinished ? media.duration : previousPosition.currentTime
    }
    loadedSourceRef.current = source
    appliedSeekRequestRef.current = seekRequest
    reportedChapterRef.current = currentChapterIndex
    media.currentTime = Math.min(currentTime, media.duration)
    onReady(timestamps.map((timestamp, index) => (timestamps[index + 1] ?? media.duration) - timestamp))
    updatePlayback(media, 'loadedmetadata')

    if (previousPosition ? !previousPosition.isPaused && !previousPosition.isFinished : !prefersReducedMotion) {
      try {
        const playing = media.play()
        updatePlayback(media, 'play')
        await playing
      } catch (error) {
        if (media.getAttribute('src') === source) updatePlayback(media, 'error')
        // Autoplay policies and interrupted play requests are expected browser behavior.
        if (error instanceof DOMException && (error.name === 'AbortError' || error.name === 'NotAllowedError')) return
        window.reportError(error)
      }
    }
  })

  useEffect(() => {
    const media = videoRef.current
    if (!prefersReducedMotion || !media) return

    media.pause()
    isPlayingRef.current = false
    const state = {
      currentTime: playbackStateRef.current?.currentTime ?? media.currentTime,
      isPaused: true,
      isPlaying: false,
      isFinished: playbackStateRef.current?.isFinished ?? media.ended,
    }
    playbackStateRef.current = state
    onPlaybackChange(state)
  }, [prefersReducedMotion, videoRef, playbackStateRef, onPlaybackChange])

  useEffect(() => {
    const media = videoRef.current
    if (!media) return

    if (loadedSourceRef.current !== videoUrl) {
      loadedSourceRef.current = null
      isPlayingRef.current = false
    }
    const handleMetadata = () => void initializeMedia(media)
    media.addEventListener('loadedmetadata', handleMetadata)
    // Metadata may already have loaded before React hydrated the video.
    handleMetadata()
    return () => media.removeEventListener('loadedmetadata', handleMetadata)
  }, [videoRef, videoUrl])

  useEffect(() => {
    const media = videoRef.current
    if (!media || loadedSourceRef.current !== videoUrl) return
    if (appliedSeekRequestRef.current === seekRequest && reportedChapterRef.current === currentChapterIndex) return

    const timestamp = video?.chapterTimestamps[currentChapterIndex]
    if (timestamp === undefined) {
      window.reportError(new Error(`Hero video has no timestamp for chapter ${currentChapterIndex}.`))
      return
    }
    appliedSeekRequestRef.current = seekRequest
    reportedChapterRef.current = currentChapterIndex
    media.currentTime = timestamp
  }, [currentChapterIndex, seekRequest, video, videoRef, videoUrl])

  const handleError = (event: SyntheticEvent<HTMLVideoElement>) => {
    const media = event.currentTarget
    media.pause()
    isPlayingRef.current = false
    const state = {
      currentTime: playbackStateRef.current?.currentTime ?? media.currentTime,
      isPaused: true,
      isPlaying: false,
      isFinished: false,
    }
    playbackStateRef.current = state
    onPlaybackChange(state)
    window.reportError(new Error(`Hero video failed to load (media error ${media.error?.code}).`))
  }

  return {videoUrl, reportPlayback, handleError}
}
